import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatCard } from './StatCard';
import { Clock, MousePointer2 } from 'lucide-react';
import { fetchOverviewData, setOverviewPeriod } from '../store/analyticsSlice';
import type { AnalyticsPeriod } from '../types';
import { calculateAnalyticsDateRange } from '@/lib/dateUtils.ts';
import UsersGroup from '@/components/icons/UsersGroup';

/**
 * Grid of KPI cards with integrated period selection.
 * Triggers re-fetching with specific date ranges when period changes.
 */
export const OverviewKpiGrid: React.FC = () => {
  const { t } = useTranslation('analytics');
  const dispatchAction = useAppDispatch();
  const currentWorkspaceIdentifier = useAppSelector(
    (state) => state.workspace.currentWorkspace?.id,
  );
  const { stats, period } = useAppSelector((state) => state.analytics.overview);

  const handlePeriodSelectionChange = (selectedPeriodValue: AnalyticsPeriod) => {
    dispatchAction(setOverviewPeriod(selectedPeriodValue));

    if (currentWorkspaceIdentifier) {
      const { start: calculatedStartDateIso, end: calculatedEndDateIso } =
        calculateAnalyticsDateRange(selectedPeriodValue);

      dispatchAction(
        fetchOverviewData({
          workspaceId: currentWorkspaceIdentifier,
          from: calculatedStartDateIso,
          to: calculatedEndDateIso,
        }),
      );
    }
  };

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium text-dark">{t('overview.title')}</h2>
        <Select
          value={period}
          onValueChange={(value) => handlePeriodSelectionChange(value as AnalyticsPeriod)}
        >
          <SelectTrigger className="w-fit min-w-[140px] bg-white h-9 text-sm rounded-[8px] border-neutral-100 px-4">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="TOTAL">{t('overview.period.total')}</SelectItem>
            <SelectItem value="YEAR">{t('overview.period.year')}</SelectItem>
            <SelectItem value="MONTH">{t('overview.period.month')}</SelectItem>
            <SelectItem value="WEEK">{t('overview.period.week')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 items-stretch">
        <div className="h-full">
          <StatCard
            icon={
              <img
                src="/assets/ticketStar.svg"
                alt={t('overview.kpi.registrations.iconAlt')}
                className="size-6"
              />
            }
            value={stats?.totalRegistrations || 0}
            label={t('overview.kpi.registrations.label')}
            subLabel={t('overview.kpi.registrations.sub')}
          />
        </div>
        <div className="h-full">
          <StatCard
            icon={<UsersGroup className="size-6" />}
            value={stats?.totalUniqueAttendees || 0}
            label={t('overview.kpi.attendees.label')}
            subLabel={t('overview.kpi.attendees.sub')}
          />
        </div>
        <div className="h-full">
          <StatCard
            icon={
              <img
                src="/assets/find.svg"
                alt={t('overview.kpi.attendanceRate.iconAlt')}
                className="size-6"
              />
            }
            value={`${stats?.averageAttendanceRate || 0}%`}
            label={t('overview.kpi.attendanceRate.label')}
            subLabel={t('overview.kpi.attendanceRate.sub')}
          />
        </div>
        <div className="h-full">
          <StatCard
            icon={<MousePointer2 className="size-6" />}
            value={stats?.totalOfferClicks || 0}
            label={t('overview.kpi.offerClicks.label')}
            subLabel={t('overview.kpi.offerClicks.sub')}
          />
        </div>
        <div className="h-full">
          <StatCard
            icon={<Clock size={20} />}
            value={stats?.totalWatchTimeHours || 0}
            label={t('overview.kpi.watchTime.label')}
            subLabel={t('overview.kpi.watchTime.sub')}
          />
        </div>
      </div>
    </section>
  );
};
